import React, { useEffect, useState } from 'react';
import type { GeoSectionBlock } from '../../lib/api/geologist';
import type { GeoUiLang } from './geoWorkspaceI18n';

// P7.4.3 — geological cross-section (разрез). Horizons on a TRUE elevation axis;
// band = real height interval, bar length ∝ reserves, colour = dominant mark.
// Draws only what the backend measured — no invented geometry.

function fmt(n: number | null | undefined, lang: GeoUiLang): string {
  if (n === null || n === undefined) return '—';
  const unit = lang === 'hy' ? 'տ' : 't';
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)} ${lang === 'ru' ? 'млн т' : lang === 'hy' ? 'մլն տ' : 'M t'}`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(0)} ${lang === 'ru' ? 'тыс т' : lang === 'hy' ? 'հազ. տ' : 'K t'}`;
  return `${n.toLocaleString(lang === 'ru' ? 'ru-RU' : lang === 'hy' ? 'hy-AM' : 'en-US')} ${unit}`;
}

const GeoSection: React.FC<{ s: GeoSectionBlock; lang?: GeoUiLang }> = ({ s, lang = 'ru' }) => {
  const [on, setOn] = useState(false);
  useEffect(() => { const t = setTimeout(() => setOn(true), 60); return () => clearTimeout(t); }, []);

  const AXIS = 78;                 // left gutter for elevation labels
  const BARMAX = 300;              // px at max reserves
  const PLOT_H = Math.max(300, s.layers.length * 34);
  const PAD = 14;
  const span = Math.max(1, s.elevation_max - s.elevation_min);
  const yOf = (e: number) => PAD + ((s.elevation_max - e) / span) * PLOT_H;
  const W = AXIS + BARMAX + 130;
  const H = PLOT_H + PAD * 2;

  const ticks: number[] = [];
  for (let e = Math.ceil(s.elevation_max / 10) * 10; e >= s.elevation_min; e -= 10) ticks.push(e);

  return (
    <div data-testid="preview-geosection" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 8, padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
        <div style={{ fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--ink-tertiary)',
          fontWeight: 600 }}>{s.title}</div>
        <div style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{s.height_system}</div>
      </div>

      <div style={{ overflowX: 'auto', marginTop: 12 }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          {/* elevation axis */}
          <line x1={AXIS} y1={PAD} x2={AXIS} y2={PAD + PLOT_H} stroke="var(--border)" strokeWidth="1.5" />
          {ticks.map((e) => (
            <g key={e}>
              <line x1={AXIS - 4} y1={yOf(e)} x2={AXIS} y2={yOf(e)} stroke="var(--border)" strokeWidth="1" />
              <text x={AXIS - 8} y={yOf(e) + 3} textAnchor="end" fontSize="10"
                fill="var(--ink-tertiary)" fontFamily="var(--font-mono)">{e}</text>
            </g>
          ))}
          <text x={18} y={PAD + PLOT_H / 2} fontSize="10" fill="var(--ink-tertiary)"
            transform={`rotate(-90 18 ${PAD + PLOT_H / 2})`} textAnchor="middle">
            {lang === 'ru' ? 'высота, м' : lang === 'hy' ? 'բարձրություն, մ' : 'elevation, m'}
          </text>

          {/* horizon bands */}
          {s.layers.map((L, i) => {
            const yT = yOf(L.elevation_top);
            const yB = yOf(L.elevation_bottom);
            const bh = Math.max(6, yB - yT - 2);
            const full = (L.reserve_total_t || 0) / (s.max_reserve_t || 1);
            const bw = on ? Math.max(2, full * BARMAX) : 0;
            return (
              <g key={i}>
                {/* faint layer extent */}
                <rect x={AXIS} y={yT} width={BARMAX} height={bh} fill={L.dominant_color} opacity={0.08} />
                {/* reserve bar */}
                <rect x={AXIS} y={yT} width={bw} height={bh} fill={L.dominant_color} rx={2}
                  style={{ transition: `width .8s cubic-bezier(.2,.8,.2,1) ${i * 45}ms` }}>
                  <title>{`${L.label} m · ${fmt(L.reserve_total_t, lang)}${L.dominant_mark ? ' · ' + L.dominant_mark : ''}`}</title>
                </rect>
                {/* interval label at band centre on the axis */}
                <text x={AXIS - 8} y={(yT + yB) / 2 + 3} textAnchor="end" fontSize="9.5"
                  fill="var(--ink-secondary)" fontFamily="var(--font-mono)"
                  style={{ opacity: bh >= 16 ? 1 : 0 }}>{L.label}</text>
                {/* reserves + dominant mark */}
                <text x={AXIS + Math.max(bw, 2) + 8} y={(yT + yB) / 2 + 3} fontSize="11"
                  fill="var(--ink-primary)" style={{ opacity: on ? 1 : 0, transition: `opacity .5s ${i * 45 + 300}ms` }}>
                  {fmt(L.reserve_total_t, lang)}
                  {L.dominant_mark && (
                    <tspan fill={L.dominant_color} fontWeight={700}>{'  '}{L.dominant_mark}</tspan>
                  )}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* mark legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6, fontSize: 12 }}>
        {Array.from(new Map(s.layers.filter((L) => L.dominant_mark)
          .map((L) => [L.dominant_mark, L.dominant_color])).entries()).map(([mark, color]) => (
          <span key={mark} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--ink-secondary)' }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: color as string }} />{mark}
          </span>
        ))}
      </div>

      {s.disclaimer && <div style={{ fontSize: 12, color: 'var(--ink-tertiary)', marginTop: 10 }}>{s.disclaimer}</div>}
      {s.note && <div style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 4 }}>{s.note}</div>}
    </div>
  );
};

export default GeoSection;
